import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

export type ImpersonatedRole = "retailer" | "support";

export interface ImpersonatedRetailer {
  id: string;
  store_name: string;
  email: string | null;
}

interface ImpersonationState {
  impersonating: boolean;
  impersonatedRole: ImpersonatedRole | null;
  impersonatedRetailer: ImpersonatedRetailer | null;
}

interface ImpersonationContextType extends ImpersonationState {
  startImpersonation: (role: ImpersonatedRole, retailer?: ImpersonatedRetailer) => void;
  stopImpersonation: () => void;
}

/* ------------------------------------------------------------------ */
/* Storage key                                                          */
/* ------------------------------------------------------------------ */

const STORAGE_KEY = "fyrris_impersonation";

function readStorage(): ImpersonationState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { impersonating: false, impersonatedRole: null, impersonatedRetailer: null };
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return { impersonating: false, impersonatedRole: null, impersonatedRetailer: null };
  }
}

function writeStorage(state: ImpersonationState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function clearStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/* ------------------------------------------------------------------ */
/* Context                                                              */
/* ------------------------------------------------------------------ */

const ImpersonationContext = createContext<ImpersonationContextType>({
  impersonating: false,
  impersonatedRole: null,
  impersonatedRetailer: null,
  startImpersonation: () => {},
  stopImpersonation: () => {},
});

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

/* ------------------------------------------------------------------ */
/* Provider                                                             */
/* ------------------------------------------------------------------ */

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ImpersonationState>(readStorage);

  // Sync to sessionStorage whenever state changes
  useEffect(() => {
    if (state.impersonating) {
      writeStorage(state);
    } else {
      clearStorage();
    }
  }, [state]);

  const startImpersonation = useCallback(
    (role: ImpersonatedRole, retailer?: ImpersonatedRetailer) => {
      setState({
        impersonating: true,
        impersonatedRole: role,
        impersonatedRetailer: retailer ?? null,
      });
    },
    []
  );

  const stopImpersonation = useCallback(() => {
    setState({ impersonating: false, impersonatedRole: null, impersonatedRetailer: null });
  }, []);

  const value = useMemo(
    () => ({ ...state, startImpersonation, stopImpersonation }),
    [state, startImpersonation, stopImpersonation]
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}
